import { useParams } from "react-router";
import { standBezeichnung } from "@dj/domain/revier";
import { Symbol } from "../design/Symbol";
import type { Zeile } from "../plattform/lesemodelle";
import { AnsichtDerDrueckjagd } from "./AnsichtDerDrueckjagd";
import { Leer, useMeineAufgaben, useRevier, useRevierSammlung, wert } from "./teile";

/**
 * Standansicht (oberflaeche.md §6, F-206): der eigene Stand mit dem Revier, in dem er liegt, und genau dessen Freigabe —
 * je Freigabeposition eine Zeile je Kontingent der Wildart (ohne Kontingent eine Zeile mit „—“). Bei einer gemeinsamen
 * Drückjagd gilt damit die Freigabe des Reviers des Standes, nie die eines anderen Reviers (ADR-0011). Zusammengesetzt
 * aus Meine Aufgaben, Freigabe und Kontingentstand des Reviers (lesemodelle.md L-15, kein eigenes Lesemodell).
 */

const SPALTEN = ["Stand", "Revier", "Wildart", "Freigabestatus", "Kontingent", "Stückzahl", "Rest"] as const;

interface Standzeile {
  schluessel: string;
  position: Zeile;
  kontingent: Zeile | null;
}

function zeilenDerFreigabe(positionen: readonly Zeile[], kontingente: readonly Zeile[]): Standzeile[] {
  return positionen.flatMap((p): Standzeile[] => {
    const eigene = kontingente.filter((k) => k["Wildart"] === p["Wildart"]);
    return eigene.length
      ? eigene.map((k) => ({ schluessel: `${p._id}/${k._id}`, position: p, kontingent: k }))
      : [{ schluessel: p._id, position: p, kontingent: null }];
  });
}

export function Standansicht() {
  const { id = "" } = useParams();
  const aufgaben = useMeineAufgaben(id);
  const revier = useRevier(id);
  const positionen = useRevierSammlung(id, revier, "freigabe");
  const kontingente = useRevierSammlung(id, revier, "kontingentstand");
  const stand = aufgaben.daten?.["Stand"];
  const reviername = aufgaben.daten?.["Revier"];
  const zeilen = zeilenDerFreigabe(positionen.daten, kontingente.daten);
  return (
    <AnsichtDerDrueckjagd drueckjagdId={id} name="Standansicht">
      {stand ? (
        <article className="eintrag platte" aria-label={`Stand ${String(stand)}`}>
          <h3><Symbol name="stand" />{standBezeichnung(String(stand), aufgaben.daten?.["Standname"] as string | null | undefined)}</h3>
          <div className="eintrag__meta">{wert(reviername)}</div>
          {zeilen.length ? (
            <table className="tabelle">
              <thead><tr>{SPALTEN.map((s) => <th key={s} scope="col">{s}</th>)}</tr></thead>
              <tbody>
                {zeilen.map(({ schluessel, position, kontingent }) => {
                  const gesperrt = position["Freigabestatus"] === "gesperrt";
                  return (
                    <tr key={schluessel}>
                      <td>{String(stand)}</td>
                      <td>{wert(reviername)}</td>
                      <td>{wert(position["Wildart"])}</td>
                      <td><span className={gesperrt ? "marke-status marke-status--stark" : "marke-status"}>{wert(position["Freigabestatus"])}</span></td>
                      <td>{wert(kontingent?.["Kontingent"])}</td>
                      <td>{wert(kontingent?.["Stückzahl"])}</td>
                      <td>{wert(kontingent?.["Rest"])}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : revier && !positionen.laedt ? <Leer text="Noch keine Freigabe" symbol="freigabe" /> : null}
        </article>
      ) : !aufgaben.laedt ? <Leer text="Kein Stand zugewiesen" symbol="stand" /> : null}
    </AnsichtDerDrueckjagd>
  );
}
